import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Autocomplete, Box, InputAdornment, TextField, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";
import { FS } from "@plannel/utils/font-util";
import { publishScreenVersion, retractScreenVersion } from "@plannel/components/agent/screenVersionChannel";

/**
 * 버전 선택 Autocomplete의 프레젠테이션 껍데기 (Dp/Mp/Rp/Ip VersionFilter 공용).
 *
 * 데이터 조회·정렬·redux·상태코드 체계는 모듈마다 달라 각 필터가 그대로 보유하고,
 * 이 컴포넌트는 렌더링만 담당한다. 옵션은 { id, name, statusCd, ... } 형태를 가정하며,
 * 아이콘/색/툴팁(기간)은 함수 prop으로 주입한다.
 *
 * 강화 렌더:
 *  - 상태별 그룹 헤더 (statusCd로 클러스터링, 색은 getIconColor)
 *  - 옵션 행: 상태 아이콘 + 이름(검색어 하이라이트) + 기간(2번째 줄) + 상태 배지
 *
 * @param {object[]} options 버전 목록
 * @param {object} value 선택된 버전
 * @param {function} onChange (event, value) => void
 * @param {string} label 입력 필드 라벨 (보통 t("version"))
 * @param {function} getIcon (option) => MUI 아이콘 컴포넌트 (없으면 생략)
 * @param {function} getIconColor (option) => color — 상태 색 (배지·그룹 헤더에 재사용)
 * @param {function} [getTooltip] (option) => 기간 문자열 (옵션 2번째 줄 + 입력 hover)
 * @param {string} [tooltipPlacement="top"] 입력 hover 툴팁 위치
 * @param {number} [minWidth=240]
 * @param {boolean} [showInputIcon=true] 입력 필드 좌측 아이콘 표시 여부 (IP는 false)
 * @param {object} [inputIconSx] 입력 필드 아이콘 추가 sx (DP는 ml:1)
 * @param {function} [isOptionEqualToValue] 기본은 id 비교 (IP는 name 비교)
 * @param {boolean} [publishToChat=true] 선택한 버전을 챗의 화면 버전 채널에 싣는다 — 화면이 보고 있는
 *   계획을 고르는 선택기만 true. 모달의 소스 데이터·상위 계획 연결 선택기는 false.
 * @param {string} [moduleCd] 발행 슬롯 모듈. 생략하면 선택값의 moduleCd 를 쓴다.
 */

// 버전 상태코드 → lang-pack(menu 섹션) i18n 키. 기존 키 재사용 + 신규(planned/unplanned/failed/stale/na).
const STATUS_I18N_KEY = {
  RELEASED: "released",
  RELEASING: "releasing",
  RUNNING: "running",
  READY: "readyLabel",
  DEFAULT: "default",
  SCENARIO: "scenarioColumnLabel",
  REFERENCE: "mpReferenceLabel",
  PLANNED: "planned",
  UNPLANNED: "unplanned",
  FAILED: "failed",
  STALE: "stale",
  NA: "na",
};

// i18n 미등록 상태는 "RELEASED" → "Released" 형태로 폴백
const titleCase = (statusCd) => String(statusCd ?? "")
  .split("_")
  .filter(Boolean)
  .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
  .join(" ") || "-";

const VersionAutocomplete = ({
  options,
  value,
  onChange,
  label,
  getIcon,
  getIconColor,
  getTooltip,
  tooltipPlacement = "top",
  minWidth = 240,
  showInputIcon = true,
  inputIconSx,
  disabled = false,
  disableGroup = false,
  sx,
  isOptionEqualToValue = (option, val) => option.id === val?.id,
  publishToChat = true,
  moduleCd,
}) => {
  const { t } = useTranslation();

  // 화면 버전 채널 — 선택 즉시 발행하고, 이 선택기가 사라지면 자기 토큰만 회수한다.
  //   redux 저장은 화면마다 시점이 달라 낡을 수 있어 여기서 직접 싣는다(screenVersionChannel 주석).
  const slotModule = moduleCd ?? value?.moduleCd;
  useEffect(() => {
    if (!publishToChat || value?.id == null || !slotModule) return undefined;
    const token = publishScreenVersion(slotModule, value);
    return () => retractScreenVersion(slotModule, token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishToChat, slotModule, value?.id, value?.name, value?.statusCd]);

  // 상태 라벨 — lang-pack 우선, 미등록 시 title-case 폴백
  const statusLabel = (statusCd) => {
    const key = STATUS_I18N_KEY[statusCd];
    if (key) {
      const label = t(key);
      if (label && label !== key) return label;
    }
    return titleCase(statusCd);
  };

  // 상태(statusCd)별로 클러스터링 — 첫 등장 순서를 보존해 각 필터의 정렬 의도를 존중.
  const groupedOptions = useMemo(() => {
    const order = [];
    const buckets = new Map();
    for (const opt of options ?? []) {
      const key = opt?.statusCd ?? "";
      if (!buckets.has(key)) { buckets.set(key, []); order.push(key); }
      buckets.get(key).push(opt);
    }
    return order.flatMap((key) => buckets.get(key));
  }, [options]);

  // statusCd → 대표 색 (getIconColor 재사용). 그룹 헤더에서 사용.
  const statusColor = useMemo(() => {
    const map = new Map();
    for (const opt of options ?? []) {
      const key = opt?.statusCd ?? "";
      if (!map.has(key)) map.set(key, getIconColor?.(opt));
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  const renderIcon = (option, sx) => {
    const IconComp = getIcon?.(option);
    if (!IconComp) return null;
    return <Box component={IconComp} sx={{ color: getIconColor?.(option), ...sx }} />;
  };

  const statusBadge = (statusCd, color) => (
    <Box component="span" sx={{
      flexShrink: 0, fontSize: FS.CAPTION, fontWeight: 600, lineHeight: 1.6,
      px: 0.75, borderRadius: 5, whiteSpace: "nowrap",
      color: color ?? "text.secondary",
      bgcolor: color ? alpha(color, 0.14) : "action.hover",
    }}>
      {statusLabel(statusCd)}
    </Box>
  );

  // 닫힌 입력창의 선택값이 잘려도 hover로 전체 이름+기간을 볼 수 있게 툴팁 구성
  const fieldTooltip = value
    ? [value.name, getTooltip?.(value)].filter(Boolean).join("  ·  ")
    : "";

  return (
    <Tooltip title={fieldTooltip} placement={tooltipPlacement}>
      <Autocomplete
        options={disableGroup ? (options ?? []) : groupedOptions}
        getOptionLabel={(option) => option?.name ?? ""}
        value={value}
        onChange={onChange}
        isOptionEqualToValue={isOptionEqualToValue}
        groupBy={disableGroup ? undefined : (option) => option?.statusCd ?? ""}
        size="small"
        disableClearable
        disabled={disabled}
        sx={{ mr: 1, minWidth, ...sx }}
        renderGroup={(params) => {
          // 상태(statusCd) 없는 옵션(예: Master Data)은 그룹 헤더 없이 렌더
          if (!params.group) {
            return <li key={params.key}><Box component="ul" sx={{ p: 0 }}>{params.children}</Box></li>;
          }
          const color = statusColor.get(params.group);
          return (
            <li key={params.key}>
              <Box sx={{
                position: "sticky", top: -4, zIndex: 1,
                display: "flex", alignItems: "center", gap: 0.75,
                px: 1.5, py: 0.5, bgcolor: "background.paper", borderBottom: 1, borderColor: "grey.100",
              }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, bgcolor: color ?? "grey.400" }} />
                <Typography sx={{ fontSize: FS.BODY, fontWeight: 700, letterSpacing: 0.4, color: color ?? "text.secondary" }}>
                  {statusLabel(params.group)}
                </Typography>
              </Box>
              <Box component="ul" sx={{ p: 0 }}>{params.children}</Box>
            </li>
          );
        }}
        renderOption={(props, option, { inputValue }) => {
          const period = getTooltip?.(option);
          const color = getIconColor?.(option);
          const parts = parse(option.name ?? "", match(option.name ?? "", inputValue ?? "", { insideWords: true }));
          return (
            <Box component="li" {...props} key={option.id ?? option.name} sx={{ flexDirection: "column", alignItems: "stretch !important", gap: 0.25 }}>
              {/* 1번째 줄: 아이콘 + 이름(잘림·hover 툴팁) + 상태 배지 */}
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", width: "100%" }}>
                {renderIcon(option, { width: 16, height: 16, flexShrink: 0 })}
                <Tooltip title={option.name} placement="top">
                  <Box sx={{ flex: 1, minWidth: 0, fontSize: FS.TITLE, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {parts.map((part, idx) => part.highlight
                      ? <Box key={idx} component="span" sx={{ color: "primary.main", fontWeight: 700 }}>{part.text}</Box>
                      : part.text)}
                  </Box>
                </Tooltip>
                {option.statusCd ? statusBadge(option.statusCd, color) : null}
              </Box>
              {/* 2번째 줄: 기간 — 행 전체 너비를 써서 잘리지 않게 */}
              {period && (
                <Typography sx={{ fontSize: FS.BODY, color: "text.secondary", pl: "24px", whiteSpace: "nowrap" }}>
                  {period}
                </Typography>
              )}
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            InputProps={showInputIcon && value ? {
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  {renderIcon(value, { width: 18, height: 18, ...inputIconSx })}
                </InputAdornment>
              ),
            } : params.InputProps}
          />
        )}
      />
    </Tooltip>
  );
};

export default VersionAutocomplete;
